import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineMenu from '~icons/ic/baseline-menu'
import Logo from 'src/components/Logo'
import { cn } from 'src/utils/cx'
import { NAV_HREFS, NAV_LINK_KEYS } from '../constants'
import css from '../Landing.module.css'

export function Navbar() {
  const { t } = useTranslation('blackboard', { keyPrefix: 'welcome' })
  const [scrolled, setScrolled] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50)
      const scrollableHeight = document.body.scrollHeight - window.innerHeight
      const pct =
        scrollableHeight > 0 ? (window.scrollY / scrollableHeight) * 100 : 0
      setScrollProgress(Math.min(pct, 100))
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const scrollTo = useCallback((href: string) => {
    const id = href.replace('#', '')
    const el = document.getElementById(id)
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' })
    }
    setMobileOpen(false)
  }, [])

  return (
    <>
      {/* Scroll progress bar */}
      <div
        className={css.scrollProgress}
        style={{ width: `${scrollProgress}%` }}
      />

      {/* Nav pill */}
      <nav
        className={cn(
          css.navGlass,
          scrolled && css.navScrolled,
          'fixed left-1/2 top-5 z-[1000] flex -translate-x-1/2 items-center gap-1 rounded-full border border-gray-200 py-2 pl-6 pr-2 shadow-md transition-shadow',
        )}
      >
        <a
          href="#hero"
          onClick={e => {
            e.preventDefault()
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
          className="mr-4 flex shrink-0 items-center gap-2 text-lg font-pixai-rounded font-medium text-gray-900"
        >
          <Logo className="size-7" />
          PixAI
        </a>

        {NAV_LINK_KEYS.map(key => (
          <a
            key={key}
            href={NAV_HREFS[key]}
            onClick={e => {
              e.preventDefault()
              scrollTo(NAV_HREFS[key])
            }}
            className="hidden whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 md:block"
          >
            {t(`nav.${key}`)}
          </a>
        ))}

        <a
          href="#experience"
          onClick={e => {
            e.preventDefault()
            scrollTo('#experience')
          }}
          className="ml-1 hidden whitespace-nowrap rounded-full bg-[--landing-pink] px-6 py-2.5 text-sm font-semibold text-white transition-all hover:scale-[1.03] hover:bg-[--landing-pink-dark] md:block"
        >
          {t('nav.try-now')}
        </a>

        <button
          onClick={() => setMobileOpen(v => !v)}
          className="block p-2 text-gray-600 md:hidden"
          aria-label={t('nav.menu-label')}
        >
          {mobileOpen ? (
            <IcBaselineClose className="size-6" />
          ) : (
            <IcBaselineMenu className="size-6" />
          )}
        </button>
      </nav>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div
          className={cn(
            css.mobileDropdown,
            'fixed left-4 right-4 top-[72px] z-[999] flex flex-col rounded-2xl border border-gray-200 bg-white/97 p-2 shadow-xl backdrop-blur-2xl',
          )}
        >
          {NAV_LINK_KEYS.map(key => (
            <a
              key={key}
              href={NAV_HREFS[key]}
              onClick={e => {
                e.preventDefault()
                scrollTo(NAV_HREFS[key])
              }}
              className="rounded-xl px-5 py-3.5 text-base font-medium text-gray-700 transition-colors hover:bg-[--landing-pink-bg]"
            >
              {t(`nav.${key}`)}
            </a>
          ))}
          <a
            href="#experience"
            onClick={e => {
              e.preventDefault()
              scrollTo('#experience')
            }}
            className="mt-1 rounded-xl bg-[--landing-pink] px-5 py-3.5 text-center font-bold text-white transition-colors hover:bg-[--landing-pink-dark]"
          >
            {t('nav.try-now')}
          </a>
        </div>
      )}
    </>
  )
}
